import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Eye, X, Play, ExternalLink, MapPin, Palette, Target, Zap, MessageCircle, Globe, Phone, Mail, Calendar, Package, DollarSign, Clock, Link, Plus, Trash2, Download, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type OrderStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

interface VideoConfig {
  id: number;
  language: string;
  goal: string;
  vibe: string;
  customVibe?: string;
  letUsChoose?: boolean;
  offerDetails?: string;
  productName?: string;
  productUrl?: string;
  keyFeatures?: string;
  eventName?: string;
  eventDate?: string;
  eventLocation?: string;
  ticketLink?: string;
  targetUrl?: string;
  socialHandle?: string;
}

interface Order {
  id: string;
  created_at: string;
  business_name: string;
  customer_email: string;
  pack_type: string | null;
  amount_paid: number;
  currency: string;
  payment_status: string;
  status: OrderStatus;
  status_updated_at: string;
  admin_notes: string | null;
  city: string | null;
  industry: string;
  whatsapp_number: string | null;
  main_goal: string | null;
  business_goals: string;
  content_objectives: string;
  special_requests: string | null;
  add_ons: any;
  video_configs: VideoConfig[] | null;
  logo_urls: string[] | null;
  store_photo_urls: string[] | null;
  product_image_urls: string[] | null;
  voice_note_url: string | null;
  brand_color: string | null;
  delivery_preference: string | null;
  vibe: string | null;
  target_audience: string | null;
  platforms: string[] | null;
  call_to_action: string | null;
  google_maps_link: string | null;
  business_type: string | null;
  creative_goal: string | null;
  inspiration: string | null;
  delivery_urls: string[] | null;
  email_sent_confirmation: boolean | null;
  email_sent_in_progress: boolean | null;
  email_sent_completed: boolean | null;
}

const statusColors: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
  in_progress: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
  completed: 'bg-green-500/20 text-green-300 border-green-500/50',
  cancelled: 'bg-red-500/20 text-red-300 border-red-500/50',
};

const languageFlags: Record<string, string> = {
  english: '🇬🇧',
  german: '🇩🇪',
  french: '🇫🇷',
  spanish: '🇪🇸',
  italian: '🇮🇹',
  arabic: '🇸🇦',
};

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [newStatus, setNewStatus] = useState<OrderStatus | ''>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [deliveryUrls, setDeliveryUrls] = useState<string[]>([]);
  const [newDeliveryUrl, setNewDeliveryUrl] = useState('');
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState<string | null>(null);
  const [resettingFlags, setResettingFlags] = useState(false);

  useEffect(() => {
    const checkAuthAndLoad = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/admin");
        return;
      }

      // Check admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .single();

      if (!roleData || roleData.role !== "admin") {
        navigate("/");
        return;
      }

      fetchOrders();
    };

    checkAuthAndLoad();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/admin");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('customer_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      // Cast the data to Order[] since video_configs comes as Json from Supabase
      setOrders((data as unknown as Order[]) || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;

    setUpdating(true);
    try {
      const { error } = await supabase
        .from('customer_orders')
        .update({
          status: newStatus,
          admin_notes: adminNotes || selectedOrder.admin_notes,
        })
        .eq('id', selectedOrder.id);

      if (error) throw error;

      // Send email notification based on status change
      if (newStatus !== selectedOrder.status) {
        let emailType: string | null = null;
        
        if (newStatus === 'in_progress') {
          emailType = 'in_progress';
        } else if (newStatus === 'completed') {
          emailType = 'completed';
        }

        if (emailType) {
          try {
            const { error: emailError } = await supabase.functions.invoke('send-order-email', {
              body: {
                orderId: selectedOrder.id,
                emailType,
              },
            });

            if (emailError) {
              console.error('Email send error:', emailError);
              toast.error('Status updated but email failed to send');
            } else {
              toast.success(`Status updated and ${emailType === 'in_progress' ? 'production' : 'completion'} email sent!`);
            }
          } catch (emailErr) {
            console.error('Email invoke error:', emailErr);
            toast.success('Order status updated (email notification may have failed)');
          }
        } else {
          toast.success('Order status updated successfully');
        }
      } else {
        toast.success('Order notes updated successfully');
      }

      setSelectedOrder(null);
      setNewStatus('');
      setAdminNotes('');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const openOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setAdminNotes(order.admin_notes || '');
    setDeliveryUrls(order.delivery_urls || []);
    setNewDeliveryUrl('');
  };

  const handleAddDeliveryUrl = () => {
    if (!newDeliveryUrl.trim()) return;
    if (!newDeliveryUrl.startsWith('http')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }
    setDeliveryUrls([...deliveryUrls, newDeliveryUrl.trim()]);
    setNewDeliveryUrl('');
  };

  const handleRemoveDeliveryUrl = (index: number) => {
    setDeliveryUrls(deliveryUrls.filter((_, i) => i !== index));
  };

  const handleSaveDeliveryUrls = async () => {
    if (!selectedOrder) return;
    setSavingDelivery(true);
    try {
      const { error } = await supabase
        .from('customer_orders')
        .update({ delivery_urls: deliveryUrls })
        .eq('id', selectedOrder.id);

      if (error) throw error;
      toast.success('Delivery URLs saved successfully');
      fetchOrders();
    } catch (error) {
      console.error('Error saving delivery URLs:', error);
      toast.error('Failed to save delivery URLs');
    } finally {
      setSavingDelivery(false);
    }
  };

  const handleSendTestEmail = async (emailType: 'confirmation' | 'in_progress' | 'completed' | 'cancelled') => {
    if (!selectedOrder) return;
    setSendingTestEmail(emailType);
    try {
      const { error } = await supabase.functions.invoke('send-order-email', {
        body: {
          orderId: selectedOrder.id,
          emailType,
        },
      });

      if (error) throw error;
      toast.success(`${emailType.replace('_', ' ')} email sent successfully!`);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(`Failed to send ${emailType} email`);
    } finally {
      setSendingTestEmail(null);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeliveryDate = (order: Order) => {
    const createdAt = new Date(order.created_at);
    const hours = order.delivery_preference === 'express' ? 24 : 72;
    const deliveryDate = new Date(createdAt.getTime() + hours * 60 * 60 * 1000);
    return deliveryDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Order Management</h1>
              <p className="text-muted-foreground">Track and manage customer orders</p>
            </div>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-muted-foreground">Loading orders...</p>
            </CardContent>
          </Card>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-muted-foreground">No orders yet</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>All Orders ({orders.length})</CardTitle>
              <CardDescription>View and update order statuses</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Pack</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">{order.business_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{order.customer_email}</div>
                          {order.city && <div className="text-muted-foreground">{order.city}</div>}
                        </div>
                      </TableCell>
                      <TableCell>{order.pack_type || 'N/A'}</TableCell>
                      <TableCell>{formatCurrency(order.amount_paid, order.currency)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[order.status]}>
                          {order.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                          {order.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={order.delivery_preference === 'express' ? 'default' : 'outline'}>
                          {order.delivery_preference === 'express' ? '⚡ 24h' : '📦 72h'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(order.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openOrderDetails(order)}
                          className="gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Order Details Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-4xl max-h-[95vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center justify-between">
              <span>Order Details - {selectedOrder?.business_name}</span>
              <div className="flex items-center gap-2">
                {selectedOrder && (
                  <>
                    <Badge className={statusColors[selectedOrder.status]}>
                      {selectedOrder.status.replace('_', ' ')}
                    </Badge>
                    <Badge variant={selectedOrder.payment_status === 'paid' ? 'default' : 'destructive'}>
                      {selectedOrder.payment_status}
                    </Badge>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="w-full justify-start px-6 border-b rounded-none bg-transparent">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="videos">Videos ({selectedOrder.video_configs?.length || 0})</TabsTrigger>
                <TabsTrigger value="assets">Assets</TabsTrigger>
                <TabsTrigger value="creative">Creative</TabsTrigger>
                <TabsTrigger value="delivery">Delivery ({deliveryUrls.length})</TabsTrigger>
                <TabsTrigger value="actions">Actions</TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[calc(95vh-180px)]">
                {/* Overview Tab */}
                <TabsContent value="overview" className="p-6 space-y-6 mt-0">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Customer Info */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Customer Info
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Email</span>
                          <span className="font-medium">{selectedOrder.customer_email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Business</span>
                          <span className="font-medium">{selectedOrder.business_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">City</span>
                          <span className="font-medium">{selectedOrder.city || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Type</span>
                          <span className="font-medium">{selectedOrder.business_type || selectedOrder.industry || 'N/A'}</span>
                        </div>
                        {selectedOrder.whatsapp_number && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">WhatsApp</span>
                            <a 
                              href={`https://wa.me/${selectedOrder.whatsapp_number.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-green-400 hover:underline flex items-center gap-1"
                            >
                              <MessageCircle className="w-3 h-3" />
                              {selectedOrder.whatsapp_number}
                            </a>
                          </div>
                        )}
                        {selectedOrder.google_maps_link && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Location</span>
                            <a 
                              href={selectedOrder.google_maps_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-400 hover:underline flex items-center gap-1"
                            >
                              <MapPin className="w-3 h-3" />
                              View Map
                            </a>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Order Info */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Package className="w-4 h-4" /> Order Info
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Pack</span>
                          <Badge variant="outline">{selectedOrder.pack_type || 'N/A'}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Amount</span>
                          <span className="font-medium text-green-400">
                            {formatCurrency(selectedOrder.amount_paid, selectedOrder.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Delivery</span>
                          <Badge variant={selectedOrder.delivery_preference === 'express' ? 'default' : 'outline'}>
                            {selectedOrder.delivery_preference === 'express' ? '⚡ Express 24h' : '📦 Standard 72h'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Due By</span>
                          <span className="font-medium text-yellow-400">{getDeliveryDate(selectedOrder)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Ordered</span>
                          <span className="font-medium">{formatDate(selectedOrder.created_at)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Add-ons */}
                  {selectedOrder.add_ons && Array.isArray(selectedOrder.add_ons) && selectedOrder.add_ons.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Zap className="w-4 h-4" /> Add-ons
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {selectedOrder.add_ons.map((addon: any, index: number) => (
                            <Badge key={index} variant="secondary" className="text-sm">
                              {typeof addon === 'string' ? addon.replace('_', ' ') : addon.name || addon}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Goals & Notes */}
                  <div className="grid grid-cols-2 gap-6">
                    {selectedOrder.main_goal && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Target className="w-4 h-4" /> Main Goal
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm">{selectedOrder.main_goal}</p>
                        </CardContent>
                      </Card>
                    )}
                    {selectedOrder.special_requests && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm">Special Requests</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedOrder.special_requests}</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>

                {/* Videos Tab */}
                <TabsContent value="videos" className="p-6 space-y-4 mt-0">
                  {selectedOrder.video_configs && selectedOrder.video_configs.length > 0 ? (
                    <div className="grid gap-4">
                      {selectedOrder.video_configs.map((config, index) => (
                        <Card key={config.id || index}>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm flex items-center justify-between">
                              <span>Video {index + 1}</span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {languageFlags[config.language?.toLowerCase()] || '🌐'} {config.language}
                                </Badge>
                                {config.letUsChoose && (
                                  <Badge className="bg-purple-500/20 text-purple-300">🧠 AI Crafted</Badge>
                                )}
                              </div>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground block">Goal</span>
                                <span className="font-medium">{config.goal}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block">Vibe</span>
                                <span className="font-medium">{config.customVibe || config.vibe}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block">Format</span>
                                <span className="font-medium">9:16 Vertical</span>
                              </div>
                            </div>

                            {/* Goal-specific fields */}
                            {config.offerDetails && (
                              <div className="pt-2 border-t">
                                <span className="text-muted-foreground text-sm block">Offer Details</span>
                                <p className="text-sm">{config.offerDetails}</p>
                              </div>
                            )}
                            {config.productName && (
                              <div className="pt-2 border-t grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground block">Product</span>
                                  <span className="font-medium">{config.productName}</span>
                                </div>
                                {config.productUrl && (
                                  <div>
                                    <span className="text-muted-foreground block">URL</span>
                                    <a href={config.productUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                                      <ExternalLink className="w-3 h-3" /> Link
                                    </a>
                                  </div>
                                )}
                                {config.keyFeatures && (
                                  <div className="col-span-2">
                                    <span className="text-muted-foreground block">Key Features</span>
                                    <p>{config.keyFeatures}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            {config.eventName && (
                              <div className="pt-2 border-t grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground block">Event</span>
                                  <span className="font-medium">{config.eventName}</span>
                                </div>
                                {config.eventDate && (
                                  <div>
                                    <span className="text-muted-foreground block">Date</span>
                                    <span>{config.eventDate}</span>
                                  </div>
                                )}
                                {config.eventLocation && (
                                  <div>
                                    <span className="text-muted-foreground block">Location</span>
                                    <span>{config.eventLocation}</span>
                                  </div>
                                )}
                                {config.ticketLink && (
                                  <div>
                                    <span className="text-muted-foreground block">Ticket Link</span>
                                    <a href={config.ticketLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                                      <ExternalLink className="w-3 h-3" /> Link
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                            {config.targetUrl && (
                              <div className="pt-2 border-t text-sm">
                                <span className="text-muted-foreground block">Target URL</span>
                                <a href={config.targetUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                                  <ExternalLink className="w-3 h-3" /> {config.targetUrl}
                                </a>
                              </div>
                            )}
                            {config.socialHandle && (
                              <div className="pt-2 border-t text-sm">
                                <span className="text-muted-foreground block">Social Handle</span>
                                <span>{config.socialHandle}</span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="py-8 text-center text-muted-foreground">
                        No video configurations found
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Assets Tab */}
                <TabsContent value="assets" className="p-6 space-y-6 mt-0">
                  {/* Logo Images */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Logo Images</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedOrder.logo_urls && selectedOrder.logo_urls.length > 0 ? (
                        <div className="grid grid-cols-4 gap-3">
                          {selectedOrder.logo_urls.map((url, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImage(url)}
                              className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors"
                            >
                              <img src={url} alt={`Logo ${index + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No logos uploaded</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Store Photos */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Store/Location Photos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedOrder.store_photo_urls && selectedOrder.store_photo_urls.length > 0 ? (
                        <div className="grid grid-cols-4 gap-3">
                          {selectedOrder.store_photo_urls.map((url, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImage(url)}
                              className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors"
                            >
                              <img src={url} alt={`Store ${index + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No store photos uploaded</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Product Images */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Product Images</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedOrder.product_image_urls && selectedOrder.product_image_urls.length > 0 ? (
                        <div className="grid grid-cols-4 gap-3">
                          {selectedOrder.product_image_urls.map((url, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImage(url)}
                              className="aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors"
                            >
                              <img src={url} alt={`Product ${index + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No product images uploaded</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Voice Note */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Play className="w-4 h-4" /> Voice Note
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedOrder.voice_note_url ? (
                        <audio controls className="w-full">
                          <source src={selectedOrder.voice_note_url} type="audio/webm" />
                          <source src={selectedOrder.voice_note_url} type="audio/mp3" />
                          Your browser does not support audio playback.
                        </audio>
                      ) : (
                        <p className="text-sm text-muted-foreground">No voice note recorded</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Creative Tab */}
                <TabsContent value="creative" className="p-6 space-y-6 mt-0">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Brand Color */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Palette className="w-4 h-4" /> Brand Color
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedOrder.brand_color ? (
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-lg border border-border"
                              style={{ backgroundColor: selectedOrder.brand_color }}
                            />
                            <span className="font-mono text-sm">{selectedOrder.brand_color}</span>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Not specified</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Target Audience */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Target className="w-4 h-4" /> Target Audience
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{selectedOrder.target_audience || 'Not specified'}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Platforms */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Globe className="w-4 h-4" /> Target Platforms
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedOrder.platforms && selectedOrder.platforms.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedOrder.platforms.map((platform, index) => (
                            <Badge key={index} variant="secondary">{platform}</Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Not specified</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* CTA & Vibe */}
                  <div className="grid grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Call to Action</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{selectedOrder.call_to_action || 'Not specified'}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">Overall Vibe</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{selectedOrder.vibe || 'Not specified'}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Creative Goal & Inspiration */}
                  {(selectedOrder.creative_goal || selectedOrder.inspiration) && (
                    <div className="grid grid-cols-2 gap-6">
                      {selectedOrder.creative_goal && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Creative Goal</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground">{selectedOrder.creative_goal}</p>
                          </CardContent>
                        </Card>
                      )}
                      {selectedOrder.inspiration && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Inspiration</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground">{selectedOrder.inspiration}</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Delivery Tab */}
                <TabsContent value="delivery" className="p-6 space-y-6 mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Link className="w-4 h-4" /> Add Delivery Links
                      </CardTitle>
                      <p className="text-xs text-muted-foreground">
                        Add Google Drive, Dropbox, or any direct download links for the completed videos
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2">
                        <Input
                          value={newDeliveryUrl}
                          onChange={(e) => setNewDeliveryUrl(e.target.value)}
                          placeholder="https://drive.google.com/file/..."
                          onKeyDown={(e) => e.key === 'Enter' && handleAddDeliveryUrl()}
                        />
                        <Button onClick={handleAddDeliveryUrl} size="icon" variant="secondary">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Expected vs Added counter */}
                      <div className="text-sm text-muted-foreground">
                        {deliveryUrls.length} of {selectedOrder.video_configs?.length || '?'} videos added
                      </div>
                    </CardContent>
                  </Card>

                  {/* URL List */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span>Delivery URLs ({deliveryUrls.length})</span>
                        <Button 
                          onClick={handleSaveDeliveryUrls} 
                          disabled={savingDelivery}
                          size="sm"
                        >
                          {savingDelivery ? 'Saving...' : 'Save URLs'}
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {deliveryUrls.length > 0 ? (
                        <div className="space-y-2">
                          {deliveryUrls.map((url, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                              <span className="text-sm font-medium text-muted-foreground w-8">#{index + 1}</span>
                              <span className="flex-1 text-sm truncate">{url}</span>
                              <a 
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleRemoveDeliveryUrl(index)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No delivery URLs added yet. Add links above to enable download buttons in customer email.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Instructions */}
                  <Card className="border-dashed">
                    <CardContent className="py-4">
                      <div className="text-sm space-y-2">
                        <p className="font-medium">📋 Workflow:</p>
                        <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                          <li>Upload finished videos to Google Drive/Dropbox</li>
                          <li>Get shareable links (make sure "Anyone with link can view")</li>
                          <li>Paste links here and click "Save URLs"</li>
                          <li>Change status to "Completed" in Actions tab</li>
                          <li>Customer receives email with download buttons</li>
                        </ol>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Actions Tab */}
                <TabsContent value="actions" className="p-6 space-y-6 mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Update Order Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="status">Status</Label>
                        <Select value={newStatus} onValueChange={(value) => setNewStatus(value as OrderStatus)}>
                          <SelectTrigger id="status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">⏳ Pending</SelectItem>
                            <SelectItem value="in_progress">🔄 In Progress</SelectItem>
                            <SelectItem value="completed">✅ Completed</SelectItem>
                            <SelectItem value="cancelled">❌ Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="notes">Admin Notes</Label>
                        <Textarea
                          id="notes"
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Add internal notes about this order..."
                          rows={4}
                        />
                      </div>

                      <Button
                        onClick={handleUpdateStatus}
                        disabled={updating || !newStatus}
                        className="w-full"
                      >
                        {updating ? 'Updating...' : 'Update Order'}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Status Timeline */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Status Timeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Order Created</span>
                          <span>{formatDate(selectedOrder.created_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Last Status Update</span>
                          <span>{formatDate(selectedOrder.status_updated_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Expected Delivery</span>
                          <span className="text-yellow-400">{getDeliveryDate(selectedOrder)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Test Emails Section */}
                  <Card className="border-neon-purple/30">
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Send className="w-4 h-4 text-neon-purple" /> Test Email Templates
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Send test emails to verify templates render correctly. Emails go to: {selectedOrder.customer_email}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start gap-2 border-purple-500/30 hover:bg-purple-500/10"
                          onClick={() => handleSendTestEmail('confirmation')}
                          disabled={sendingTestEmail !== null}
                        >
                          {sendingTestEmail === 'confirmation' ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <span>⛽</span>
                          )}
                          Confirmation
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start gap-2 border-orange-500/30 hover:bg-orange-500/10"
                          onClick={() => handleSendTestEmail('in_progress')}
                          disabled={sendingTestEmail !== null}
                        >
                          {sendingTestEmail === 'in_progress' ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <span>🔥</span>
                          )}
                          In Progress
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start gap-2 border-green-500/30 hover:bg-green-500/10"
                          onClick={() => handleSendTestEmail('completed')}
                          disabled={sendingTestEmail !== null}
                        >
                          {sendingTestEmail === 'completed' ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <span>🚀</span>
                          )}
                          Completed
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start gap-2 border-red-500/30 hover:bg-red-500/10"
                          onClick={() => handleSendTestEmail('cancelled')}
                          disabled={sendingTestEmail !== null}
                        >
                          {sendingTestEmail === 'cancelled' ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <span>😔</span>
                          )}
                          Cancelled
                        </Button>
                      </div>
                      {/* Email Status & Reset */}
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-medium">Email Flags Status:</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-neon-cyan hover:text-neon-cyan/80"
                            onClick={async () => {
                              setResettingFlags(true);
                              try {
                                const { error } = await supabase
                                  .from('customer_orders')
                                  .update({
                                    email_sent_confirmation: false,
                                    email_sent_in_progress: false,
                                    email_sent_completed: false,
                                  })
                                  .eq('id', selectedOrder.id);
                                
                                if (error) throw error;
                                
                                // Update local state
                                setSelectedOrder({
                                  ...selectedOrder,
                                  email_sent_confirmation: false,
                                  email_sent_in_progress: false,
                                  email_sent_completed: false,
                                });
                                
                                toast.success('Email flags reset - you can now test all templates');
                              } catch (error) {
                                console.error('Error resetting flags:', error);
                                toast.error('Failed to reset email flags');
                              } finally {
                                setResettingFlags(false);
                              }
                            }}
                            disabled={resettingFlags}
                          >
                            {resettingFlags ? 'Resetting...' : '🔄 Reset All Flags'}
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className={`p-2 rounded-md text-center ${selectedOrder.email_sent_confirmation ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>
                            ⛽ Confirm: {selectedOrder.email_sent_confirmation ? '✓ Sent' : 'Not sent'}
                          </div>
                          <div className={`p-2 rounded-md text-center ${selectedOrder.email_sent_in_progress ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>
                            🔥 Progress: {selectedOrder.email_sent_in_progress ? '✓ Sent' : 'Not sent'}
                          </div>
                          <div className={`p-2 rounded-md text-center ${selectedOrder.email_sent_completed ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>
                            🚀 Complete: {selectedOrder.email_sent_completed ? '✓ Sent' : 'Not sent'}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl p-2">
          {selectedImage && (
            <img src={selectedImage} alt="Full size" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
